import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { calendarConnectionsAPI } from '../services/schedulerAPI';
import '../styles/ConferenceProviderSelector.css';

const ConferenceProviderSelector = ({ value, onChange, error }) => {
  const navigate = useNavigate();
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCalendarConnections();
  }, []);

  const loadCalendarConnections = async () => {
    try {
      const connections = await calendarConnectionsAPI.list();
      setCalendarConnections(connections || []);
    } catch (error) {
      console.error('Failed to load calendar connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasGoogleCalendar = calendarConnections.some(
    conn => conn.provider === 'google' && conn.is_active
  );

  const providers = [
    {
      id: 'google_meet',
      name: 'Google Meet',
      icon: '📆',
      color: '#4285F4',
      description: 'Auto-create Google Meet links',
      requiresSetup: !hasGoogleCalendar
    },
    {
      id: 'zoom',
      name: 'Zoom',
      icon: '🎥',
      color: '#2D8CFF',
      description: 'Auto-create Zoom meeting links',
      requiresSetup: false // Zoom is app-level, always ready
    }
  ];

  return (
    <div className="conference-provider-selector">
      <label className="conference-label">
        Conference Provider
        <span className="conference-label-hint">Auto-creates meeting link when booking</span>
      </label>

      <div className="conference-providers">
        {providers.map((provider) => (
          <button
            key={provider.id}
            type="button"
            className={`conference-provider ${value === provider.id ? 'active' : ''}`}
            onClick={() => onChange(provider.id)}
            style={{
              '--provider-color': provider.color
            }}
          >
            <div className="provider-content">
              <div className="provider-icon-wrapper">
                <span className="provider-icon">{provider.icon}</span>
                {provider.requiresSetup && (
                  <span className="provider-badge">Setup Required</span>
                )}
              </div>

              <div className="provider-info">
                <div className="provider-name">{provider.name}</div>
                <div className="provider-description">{provider.description}</div>
              </div>

              {value === provider.id && (
                <div className="provider-check">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="currentColor" />
                    <path
                      d="M6 10l2.5 2.5L14 7"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {error && <div className="conference-error">{error}</div>}

      {value && (
        <div className={`conference-info-box ${value === 'google_meet' && !hasGoogleCalendar ? 'warning' : ''}`}>
          <svg className="info-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={value === 'google_meet' && !hasGoogleCalendar
                ? "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                : "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              }
            />
          </svg>
          <div className="info-content">
            {value === 'google_meet' && !hasGoogleCalendar ? (
              <>
                <strong>Google Calendar Not Connected</strong>
                <p>To auto-create Google Meet links, you need to connect your Google Calendar first.</p>
                <button
                  onClick={() => navigate('/calendar/settings')}
                  className="connect-calendar-btn"
                  type="button"
                >
                  Connect Google Calendar →
                </button>
              </>
            ) : (
              <span>
                {value === 'google_meet'
                  ? 'A Google Meet link will be automatically created and added to the calendar event when someone books this meeting.'
                  : 'A Zoom meeting link will be automatically created when someone books this meeting.'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenceProviderSelector;
